import React, { useState, useEffect } from "react";
import EventsFilter from "./EventsFilter";
import RadiusSearchInput from "./RadiusSearchInput";
import RadiusSearchMap from "./RadiusSearchMap";
import AnalyticsFilterMap from "./AnalyticsFilterMap";
import { EventsService } from "../services/EventsService";
import {
  EventFilterRequest,
  EventResponseDto,
} from "../services/Dtos/EventsDtos";
import showToast from "../helpers/notification";

type SearchMode = "filter" | "radius";

const AnalyticsMap: React.FC = () => {
  const [events, setEvents] = useState<EventResponseDto[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [showAddressFrom, setShowAddressFrom] = useState<boolean>(false);
  const [searchMode, setSearchMode] = useState<SearchMode>("filter");
  const [radiusSearchCenter, setRadiusSearchCenter] = useState<{
    lat: number;
    lng: number;
    radius: number;
  } | null>(null);
  const [clickedCoordinates, setClickedCoordinates] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [currentRadius, setCurrentRadius] = useState<number>(10);
  const [previewRadius, setPreviewRadius] = useState<{
    lat: number;
    lng: number;
    radius: number;
  } | null>(null);
  const [zoomToCoordinates, setZoomToCoordinates] = useState<{
    lat: number;
    lng: number;
    zoom?: number;
  } | null>(null);
  const [selectedStateCode, setSelectedStateCode] = useState<string | null>(null);
  const [selectedEventType, setSelectedEventType] = useState<string | null>(null);
  const [showHeatmap, setShowHeatmap] = useState<boolean>(false);

  const handleStateSelected = (stateCode: string, lat: number, lng: number) => {
    console.log(`[STATE] State selected: ${stateCode} at lat=${lat.toFixed(6)}, lng=${lng.toFixed(6)}`);
    setSelectedStateCode(stateCode);
    setZoomToCoordinates({ lat, lng, zoom: 7 });
  };

  const handleResetZoom = () => {
    console.log(`[ZOOM] Resetting zoom to default level 5`);
    setSelectedStateCode(null);
    setZoomToCoordinates(null);
  };

  const handleShowHeatmap = () => {
    console.log(`[HEATMAP] Toggling heatmap display`);
    setShowHeatmap(!showHeatmap);
  };

  const handleFilter = async (filters: EventFilterRequest) => {
    setLoading(true);
    setError(null);
    setHasSearched(true);
    setSearchMode("filter");
    setRadiusSearchCenter(null);
    // Reset selectedStateCode - it will be set by handleStateSelected if a state was selected during search
    setSelectedStateCode(null);
    setZoomToCoordinates(null);
    // Track selected event type and reset checkbox if not DELIVERED
    setSelectedEventType(filters.eventType || null);
    if (filters.eventType !== "DELIVERED") {
      setShowAddressFrom(false);
    }

    try {
      const response = await EventsService.events.filter(filters);

      if (response.success) {
        setEvents(response.data || []);
        // showToast("success", `Successfully loaded ${response.data?.length || 0} event(s)`);
      } else {
        setError(response.message || "Failed to fetch events");
        showToast("error", response.message || "Failed to fetch events");
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "An error occurred while fetching events. Please try again.";
      setError(errorMessage);
      showToast("error", errorMessage);
      console.error("Filter error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleRadiusSearch = async (lat: number, lng: number, radius: number) => {
    console.log(`[SEARCH] Starting radius search: lat=${lat.toFixed(6)}, lng=${lng.toFixed(6)}, radius=${radius} miles`);
    setLoading(true);
    setError(null);
    setHasSearched(true);
    setSearchMode("radius");
    setRadiusSearchCenter({ lat, lng, radius });
    // Clear clicked coordinates so the search zoom effect takes priority
    setClickedCoordinates(null);
    setPreviewRadius(null);
    console.log(`[SEARCH] State updated: radiusSearchCenter set, clickedCoordinates cleared`);

    try {
      console.log(`[SEARCH] Calling API: /api/v1/events/nearby with lat=${lat.toFixed(6)}, lng=${lng.toFixed(6)}, radius=${radius}`);
      const response = await EventsService.events.nearby({ lat, lng, radius });

      if (response.success) {
        console.log(`[SEARCH] API success: ${response.data?.length || 0} events returned`);
        setEvents(response.data || []);
        // showToast("success", `Found ${response.data?.length || 0} event(s) within ${radius} miles`);
      } else {
        console.log(`[SEARCH] API returned success=false: ${response.message}`);
        setError(response.message || "Failed to fetch nearby events");
        showToast("error", response.message || "Failed to fetch nearby events");
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "An error occurred while fetching nearby events. Please try again.";
      console.error(`[SEARCH] API error:`, err);
      console.error(`[SEARCH] Error message: ${errorMessage}`);
      setError(errorMessage);
      showToast("error", errorMessage);
    } finally {
      console.log(`[SEARCH] Search complete: setting loading=false`);
      setLoading(false);
    }
  };

  const handleMapClick = (lat: number, lng: number) => {
    console.log(`[CLICK] Map clicked: lat=${lat.toFixed(6)}, lng=${lng.toFixed(6)}`);
    // When map is clicked, update coordinates and clear the previous search
    // so the map can zoom to the new clicked location
    setClickedCoordinates({ lat, lng });
    setRadiusSearchCenter(null);
    // Show preview circle with current radius
    setPreviewRadius({ lat, lng, radius: currentRadius });
    console.log(`[CLICK] Preview circle shown with radius=${currentRadius} miles`);
  };

  const handleClearRadiusSearch = () => {
    console.log(`[CLEAR] Clearing radius search`);
    setRadiusSearchCenter(null);
    setClickedCoordinates(null);
    setPreviewRadius(null);
    setEvents([]);
    setHasSearched(false);
    setError(null);
    // Trigger zoom reset by setting a special flag
    setCurrentRadius(10);
    console.log(`[CLEAR] Reset complete - map should zoom to default level`);
  };

  const handleModeSwitch = (mode: SearchMode) => {
    console.log(`[MODE] Switching mode to: ${mode}`);
    setSearchMode(mode);
    setEvents([]);
    setHasSearched(false);
    setError(null);
    setRadiusSearchCenter(null);
    setClickedCoordinates(null);
    setPreviewRadius(null);
    setSelectedStateCode(null);
    setZoomToCoordinates(null);
    setSelectedEventType(null);
    setShowAddressFrom(false);
  };

  // Update preview radius circle in real-time as user changes the radius slider
  // This shows the preview circle when clicking on the map before searching
  useEffect(() => {
    if (clickedCoordinates && !radiusSearchCenter) {
      console.log(`[PREVIEW] Updating preview circle radius to ${currentRadius} miles at clicked location`);
      setPreviewRadius({
        lat: clickedCoordinates.lat,
        lng: clickedCoordinates.lng,
        radius: currentRadius,
      });
    }
  }, [currentRadius, clickedCoordinates, radiusSearchCenter]);

  // Update search radius circle in real-time as user changes the radius after search
  // This shows the search circle updating on the map when changing the radius slider
  useEffect(() => {
    if (radiusSearchCenter && radiusSearchCenter.radius !== currentRadius) {
      console.log(`[PREVIEW] Updating search radius circle from ${radiusSearchCenter.radius} to ${currentRadius} miles`);
      setRadiusSearchCenter({
        lat: radiusSearchCenter.lat,
        lng: radiusSearchCenter.lng,
        radius: currentRadius,
      });
    }
  }, [currentRadius, radiusSearchCenter]);

  return (
    <div className="container-fluid py-4">
      <div className="mb-4">
        <h1 className="text-primary fw-bold">Analytics Map</h1>
        <p className="text-muted">
          Filter and visualize delivery events on a map across your organization
        </p>
      </div>

      {/* Mode Toggle Buttons */}
      <div className="btn-group mb-3" role="group" aria-label="Search mode toggle">
        <button
          type="button"
          className={`btn ${searchMode === "filter" ? "btn-primary" : "btn-outline-primary"}`}
          onClick={() => handleModeSwitch("filter")}
        >
          <i className="bi bi-funnel me-2"></i>
          Filter Search
        </button>
        <button
          type="button"
          className={`btn ${searchMode === "radius" ? "btn-primary" : "btn-outline-primary"}`}
          onClick={() => handleModeSwitch("radius")}
        >
          <i className="bi bi-geo-alt me-2"></i>
          Radius Search
        </button>
      </div>

      {/* Conditional Filter/Radius Search Section */}
      {searchMode === "filter" ? (
        <EventsFilter onFilter={handleFilter} onStateSelected={handleStateSelected} onResetZoom={handleResetZoom} onShowHeatmap={handleShowHeatmap} loading={loading} />
      ) : (
        <RadiusSearchInput
          onSearch={handleRadiusSearch}
          onClear={handleClearRadiusSearch}
          loading={loading}
          clickedLat={clickedCoordinates?.lat}
          clickedLng={clickedCoordinates?.lng}
          initialRadius={radiusSearchCenter?.radius}
          onRadiusChange={setCurrentRadius}
        />
      )}

      {/* Error Display */}
      {error && (
        <div className="alert alert-danger alert-dismissible fade show mb-3" role="alert">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError(null)}
            aria-label="Close"
          ></button>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="spinner-border text-primary me-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted mb-0">Fetching events...</p>
        </div>
      )}

      {/* Show Address From Checkbox - Only available for DELIVERED events */}
      {!loading && hasSearched && events.length > 0 && selectedEventType === "DELIVERED" && (
        <div className="mb-3">
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id="showAddressFromCheckbox"
              checked={showAddressFrom}
              onChange={(e) => setShowAddressFrom(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="showAddressFromCheckbox">
              Show Address From
            </label>
          </div>
        </div>
      )}

      {/* Radius Search Map - Always show in radius mode (even during loading) */}
      {searchMode === "radius" && (
        <RadiusSearchMap
          onMapClick={handleMapClick}
          radiusSearchCenter={radiusSearchCenter}
          clickedCoordinates={clickedCoordinates}
          previewRadius={previewRadius}
          events={events}
        />
      )}

      {/* Analytics Filter Map - Always show in filter mode (empty by default, populated after search) */}
      {searchMode === "filter" && (
        <AnalyticsFilterMap
          events={events}
          showAddressFrom={showAddressFrom}
          zoomToCoordinates={zoomToCoordinates}
          selectedStateCode={selectedStateCode}
          showHeatmap={showHeatmap}
        />
      )}
    </div>
  );
};

export default AnalyticsMap;
